import { ComponentClassNames } from '../../util/classNames';
import { getSemanticPaletteColor } from '../../util/colors';
import {
  breakWordStyles,
  ellipsisStyles,
  getMultiLineEllipsisStyles,
  getThemeFontSizeStyles,
  preserveTextFormattingStyles,
  preventWrapStyles,
  textColors,
} from '../../util/styles/fonts';
import { getGutterValue } from '../../util/styles/gutters';
import { getPaddingStyles } from '../../util/styles/padding';
import { IRawStyle, ITheme, getRTL, memoizeFunction, mergeStyleSets, suppressRTLFlip } from '../../util/styling';

import { BlockProps, TextSize, TextWrapStyle } from './Block.types';

const getTextAlign = (textAlign: BlockProps['textAlign'], dir: BlockProps['dir']): IRawStyle['textAlign'] => {
  if (textAlign) {
    return textAlign;
  }

  if (dir === 'rtl') {
    return suppressRTLFlip('right');
  }

  if (dir === 'ltr') {
    return suppressRTLFlip('left');
  }
};

const textWrapStylesMap: Record<Exclude<TextWrapStyle, TextWrapStyle.MultiLineEllipsis>, IRawStyle> = {
  [TextWrapStyle.Ellipsis]: ellipsisStyles,
  [TextWrapStyle.BreakWords]: breakWordStyles,
  [TextWrapStyle.PreserveTextFormatting]: preserveTextFormattingStyles,
  [TextWrapStyle.BreakWordsAndPreserveTextFormatting]: {
    ...preserveTextFormattingStyles,
    ...breakWordStyles,
  },
  [TextWrapStyle.PreventWrap]: preventWrapStyles,
};

const getTextWrapStyles = (textWrap?: BlockProps['textWrap'], maxLines?: BlockProps['maxLines']): IRawStyle => {
  const hasMultilineEllipsisWithoutValidMaxLines =
    textWrap === TextWrapStyle.MultiLineEllipsis && (!maxLines || maxLines < 1);
  if (!textWrap || hasMultilineEllipsisWithoutValidMaxLines) {
    return {};
  }

  if (textWrap === TextWrapStyle.MultiLineEllipsis) {
    return getMultiLineEllipsisStyles(maxLines as number);
  }

  return textWrapStylesMap[textWrap];
};

export interface BlockClassNameProps {
  readonly backgroundColor?: BlockProps['backgroundColor'];
  readonly bottomSpacing?: BlockProps['bottomSpacing'];
  readonly className?: BlockProps['className'];
  readonly dir?: BlockProps['dir'];
  readonly horizontalPadding?: BlockProps['horizontalPadding'];
  readonly maxLines?: BlockProps['maxLines'];
  readonly padding?: BlockProps['padding'];
  readonly paddingBottom?: BlockProps['paddingBottom'];
  readonly paddingLeft?: BlockProps['paddingLeft'];
  readonly paddingRight?: BlockProps['paddingRight'];
  readonly paddingTop?: BlockProps['paddingTop'];
  readonly textAlign?: BlockProps['textAlign'];
  readonly textColor?: BlockProps['textColor'];
  readonly textSize?: BlockProps['textSize'];
  readonly textWrap?: BlockProps['textWrap'];
  readonly theme: ITheme;
  readonly topSpacing?: BlockProps['topSpacing'];
  readonly verticalPadding?: BlockProps['verticalPadding'];
}

type BlockClassNames = ComponentClassNames<'block' | 'inner'>;
type GetClassNames = (props: BlockClassNameProps) => BlockClassNames;
export const getClassNames: GetClassNames = (props) =>
  getClassNamesMemoized(
    props.backgroundColor,
    props.bottomSpacing,
    props.className,
    props.dir,
    props.horizontalPadding,
    props.maxLines,
    props.padding,
    props.paddingBottom,
    props.paddingLeft,
    props.paddingRight,
    props.paddingTop,
    props.textAlign,
    props.textColor,
    props.textSize,
    props.textWrap,
    props.theme,
    props.topSpacing,
    props.verticalPadding,
    getRTL()
  );

type GetClassNamesMemoized = (
  backgroundColor: BlockClassNameProps['backgroundColor'] | undefined,
  bottomSpacing: BlockClassNameProps['bottomSpacing'] | undefined,
  className: BlockClassNameProps['className'] | undefined,
  dir: BlockClassNameProps['dir'] | undefined,
  horizontalPadding: BlockClassNameProps['horizontalPadding'] | undefined,
  maxLines: BlockClassNameProps['maxLines'] | undefined,
  padding: BlockClassNameProps['padding'] | undefined,
  paddingBottom: BlockClassNameProps['paddingBottom'] | undefined,
  paddingLeft: BlockClassNameProps['paddingLeft'] | undefined,
  paddingRight: BlockClassNameProps['paddingRight'] | undefined,
  paddingTop: BlockClassNameProps['paddingTop'] | undefined,
  textAlign: BlockClassNameProps['textAlign'] | undefined,
  textColor: BlockClassNameProps['textColor'] | undefined,
  textSize: BlockClassNameProps['textSize'] | undefined,
  textWrap: BlockClassNameProps['textWrap'] | undefined,
  theme: BlockClassNameProps['theme'],
  topSpacing: BlockClassNameProps['topSpacing'] | undefined,
  verticalPadding: BlockClassNameProps['verticalPadding'] | undefined,
  isRTLCacheBuster: boolean
) => BlockClassNames;
const getClassNamesMemoized: GetClassNamesMemoized = memoizeFunction(
  (
    backgroundColor,
    bottomSpacing,
    className,
    dir,
    horizontalPadding,
    maxLines,
    padding,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    textAlign,
    textColor,
    textSize,
    textWrap,
    theme,
    topSpacing,
    verticalPadding
  ) => {
    const fontSizeStyles: IRawStyle = textSize ? getThemeFontSizeStyles(textSize, theme) : {};

    const maxHeightStyles: IRawStyle = {};
    if (maxLines && fontSizeStyles.lineHeight) {
      maxHeightStyles.maxHeight = `calc(${fontSizeStyles.lineHeight} * ${maxLines})`;
      maxHeightStyles.overflow = 'hidden';
    }

    const textWrapStyles = getTextWrapStyles(textWrap, maxLines);

    const iconSize = textSize ? fontSizeStyles.fontSize : undefined;
    const svgIconTopOffset = textSize === TextSize.XSmall || textSize === TextSize.Small ? 1 : undefined;

    return mergeStyleSets({
      block: [
        'y-block',
        {
          ...fontSizeStyles,
          textAlign: getTextAlign(textAlign, dir),
          backgroundColor: backgroundColor ? getSemanticPaletteColor(theme, backgroundColor) : undefined,
          color: textColor ? textColors(theme)[textColor] : undefined,
          marginTop: topSpacing ? getGutterValue(topSpacing) : undefined,
          marginBottom: bottomSpacing ? getGutterValue(bottomSpacing) : undefined,

          ...getPaddingStyles({
            padding,
            verticalPadding,
            horizontalPadding,
            paddingTop,
            paddingRight,
            paddingBottom,
            paddingLeft,
          }),

          selectors: {
            '.y-icon, .y-fluent-icon': {
              height: iconSize,
              width: iconSize,
              top: svgIconTopOffset,
            },
          },
        },
        className,
      ],
      inner: [maxHeightStyles, textWrapStyles],
    });
  }
);
